import React, { useEffect } from 'react'
import { useDesignStore } from '../../store/designStore'
import { notification } from 'antd'

interface RealisticRenderingSystemProps {
  children: React.ReactNode
}

const RealisticRenderingSystem: React.FC<RealisticRenderingSystemProps> = ({ children }) => {
  const { canvasSettings } = useDesignStore()
  
  // Apply realistic rendering effects when enabled
  useEffect(() => {
    if (canvasSettings.enableRealisticMode) {
      // Show notification when realistic mode is enabled
      notification.success({
        message: 'Realistic Visualization Enabled',
        description: 'Your design now uses AI-enhanced realistic rendering with improved lighting, shadows, and materials.',
        duration: 4,
        placement: 'topRight'
      })
    }
  }, [canvasSettings.enableRealisticMode])

  // Enhanced lighting configuration for realistic mode
  const realisticLighting = canvasSettings.enableRealisticMode ? {
    ambientIntensity: 0.3, // Softer ambient light
    directionalIntensity: 1.2, // Stronger directional light
    shadowMapSize: 4096, // Higher resolution shadows
    enableFog: true,
    fogColor: '#87CEEB',
    fogNear: 20,
    fogFar: 100
  } : {
    ambientIntensity: 0.6,
    directionalIntensity: 1,
    shadowMapSize: 2048,
    enableFog: false,
    fogColor: '#87CEEB',
    fogNear: 20,
    fogFar: 100
  }

  return (
    <>
      {/* Enhanced Lighting for Realistic Mode */}
      <ambientLight intensity={realisticLighting.ambientIntensity} />
      <directionalLight
        position={[10, 15, 5]}
        intensity={realisticLighting.directionalIntensity}
        castShadow
        shadow-mapSize-width={realisticLighting.shadowMapSize}
        shadow-mapSize-height={realisticLighting.shadowMapSize}
        shadow-camera-far={50}
        shadow-camera-left={-25}
        shadow-camera-right={25}
        shadow-camera-top={25}
        shadow-camera-bottom={-25}
        shadow-bias={-0.0001}
      />
      
      {/* Additional realistic lighting */}
      {canvasSettings.enableRealisticMode && (
        <>
          {/* Fill light from opposite side */}
          <directionalLight
            position={[-5, 10, -5]}
            intensity={0.4}
            color="#ffeaa7"
          />
          
          {/* Rim light for better object definition */}
          <directionalLight
            position={[0, 5, -10]}
            intensity={0.3}
            color="#74b9ff"
          />
          
          {/* Ground bounce light simulation */}
          <hemisphereLight
            args={["#87CEEB", "#90EE90", 0.4]}
            position={[0, -1, 0]}
          />
        </>
      )}

      {/* Fog for atmospheric depth */}
      {realisticLighting.enableFog && (
        <fog 
          attach="fog" 
          args={[realisticLighting.fogColor, realisticLighting.fogNear, realisticLighting.fogFar]} 
        />
      )}

      {children}
    </>
  )
}

export default RealisticRenderingSystem
